import { ATTACHMENT_LIMITS, type AttachmentContentType, type AttachmentInfo } from "@probetalab/contracts";
import { blobToBase64 } from "./signature.js";

/**
 * Documents attached to a study in Captura (ADR 0030): the checks and the preparation done in the
 * browser before `results.attachment.upload`. The server checks everything again (type sniffed
 * from the bytes, size, password-protected or damaged PDFs); this side only spares the chemist an
 * upload that would be refused, and shrinks the tablet camera's photos.
 */

const MB = 1024 * 1024;

/** The content type to declare, from the browser's type or, when it gives none, the extension. */
export function attachmentType(file: Pick<File, "type" | "name">): AttachmentContentType | null {
  const t = file.type.toLowerCase();
  if (t === "application/pdf") return "application/pdf";
  if (t === "image/jpeg" || t === "image/jpg" || t === "image/pjpeg") return "image/jpeg";
  if (t === "image/png") return "image/png";
  if (t) return null;
  const ext = file.name.toLowerCase().split(".").pop();
  return ext === "pdf" ? "application/pdf" : ext === "jpg" || ext === "jpeg" ? "image/jpeg" : ext === "png" ? "image/png" : null;
}

/** Why this file cannot be attached (es-MX), or null. A photo over the cap is fine: it is shrunk first. */
export function attachmentFileProblem(file: Pick<File, "type" | "name" | "size">): string | null {
  const type = attachmentType(file);
  if (!type) return "Solo se pueden adjuntar archivos PDF, JPG o PNG.";
  if (file.size === 0) return "El archivo está vacío.";
  if (type === "application/pdf" && file.size > ATTACHMENT_LIMITS.maxBytes) return `El PDF pesa ${(file.size / MB).toFixed(1)} MB; el máximo es ${ATTACHMENT_LIMITS.maxBytes / MB} MB.`;
  return null;
}

/** The size a photo is drawn at: its longest side at most `max`, proportions kept, never enlarged. */
export function scaledSize(width: number, height: number, max: number = ATTACHMENT_LIMITS.photoMaxSidePx): { width: number; height: number } {
  const s = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * s), height: Math.round(height * s) };
}

/** A photo larger than this (bytes) is re-encoded even when its pixels are within the cap. */
const PHOTO_REENCODE_BYTES = 2 * MB;

/**
 * Turns a camera photo into a JPEG of at most 2400 px on its longest side, upright (the browser
 * applies the EXIF orientation when it decodes), so a 12-megapixel tablet photo of a report
 * becomes a few hundred KB. Returns null when the browser cannot decode it here; the original is
 * then sent and the server turns it upright itself.
 */
async function shrinkPhoto(file: File): Promise<Blob | null> {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return null;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
  } catch {
    return null;
  }
  try {
    const { width, height } = scaledSize(bitmap.width, bitmap.height);
    if (width === bitmap.width && height === bitmap.height && file.size <= PHOTO_REENCODE_BYTES) return null;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#fff"; // a transparent PNG becomes white paper, not black
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85));
  } finally {
    bitmap.close?.();
  }
}

export interface PreparedAttachment {
  file_name: string;
  content_type: AttachmentContentType;
  base64: string;
}

/** The upload payload's file part, or a problem to show. */
export async function prepareAttachment(file: File): Promise<PreparedAttachment | { problem: string }> {
  const problem = attachmentFileProblem(file);
  if (problem) return { problem };
  let type = attachmentType(file)!;
  let blob: Blob = file;
  let name = file.name || "documento";
  if (type !== "application/pdf") {
    const shrunk = await shrinkPhoto(file);
    if (shrunk) {
      blob = shrunk;
      type = "image/jpeg";
      name = name.replace(/\.(png|jpe?g)$/i, "") + ".jpg";
    }
  }
  if (blob.size > ATTACHMENT_LIMITS.maxBytes) return { problem: `El archivo pesa ${(blob.size / MB).toFixed(1)} MB; el máximo es ${ATTACHMENT_LIMITS.maxBytes / MB} MB.` };
  return { file_name: name, content_type: type, base64: await blobToBase64(blob) };
}

/** "PDF · 2 páginas · 340 KB" / "Foto · 1.2 MB". */
export function attachmentSummary(a: Pick<AttachmentInfo, "content_type" | "page_count" | "byte_size">): string {
  const size = a.byte_size >= MB ? `${(a.byte_size / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(a.byte_size / 1024))} KB`;
  if (a.content_type === "application/pdf") return `PDF · ${a.page_count === 1 ? "1 página" : `${a.page_count} páginas`} · ${size}`;
  return `Foto · ${size}`;
}

/**
 * What Captura knows about each attachment id: the ones the server sent with the study, and the
 * ones uploaded on this screen and not saved yet. Keyed by id; a file never changes.
 */
const known = new Map<string, AttachmentInfo>();

export function rememberAttachment(a: AttachmentInfo): void {
  known.set(a.id, a);
}

export function attachmentById(id: string): AttachmentInfo | undefined {
  return known.get(id);
}
